/**
 * Nachrichtenzentrale — Admin/Lehrer:innen-Interface.
 *
 * Split-Layout:
 *   Links  : Conversation-Liste + „Neue Nachricht"-Button
 *   Rechts : Message-Thread + Composer mit Attachment-Upload
 *
 * DSGVO:
 *   - Soft-Delete (eigene Nachricht löschbar, Inhalt wird geleert, Audit bleibt)
 *   - Attachments werden authentifiziert ausgeliefert (Blob-URL)
 *   - Lese-Quittung wird beim Öffnen einer Conversation automatisch gesetzt
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { MessageSquare, Plus, Send, Paperclip, X, Search, Loader2, Trash2, Users as UsersIcon, Download, Check, Archive } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { formatRelativeDateTime } from '@/lib/utils';
import MessageDeliveryStatus from '@/components/messages/MessageDeliveryStatus';

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
}

function formatMessageTime(iso) {
  return formatRelativeDateTime(iso);
}

export default function MessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) setCurrentUserId(JSON.parse(stored).id);
  }, []);

  const reloadConvs = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const r = await api.get('/messages/conversations');
      setConversations(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konversationen konnten nicht geladen werden');
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => { reloadConvs(); }, [reloadConvs]);

  const reloadMsgs = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/messages/conversations/${convId}/messages`, { params: { limit: 200 } });
      setMessages(r.data);
      const incoming = r.data.filter((m) => m.sender.id !== currentUserId && !m.deleted_at);
      await Promise.all(incoming.map(async (m) => {
        await api.post(`/messages/messages/${m.id}/delivered`).catch(() => {});
        await api.post(`/messages/messages/${m.id}/read`).catch(() => {});
      }));
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nachrichten konnten nicht geladen werden');
    } finally {
      setLoadingMsgs(false);
    }
  }, [currentUserId]);

  useEffect(() => { if (activeConvId) reloadMsgs(activeConvId); }, [activeConvId, reloadMsgs]);

  const activeConv = useMemo(
    () => conversations.find((c) => c.id === activeConvId),
    [conversations, activeConvId]
  );

  const sendMessage = async (body, files) => {
    if (!activeConvId) return;
    if ((!body || !body.trim()) && (!files || files.length === 0)) return;
    const form = new FormData();
    form.append('body', body || '');
    (files || []).forEach((f) => form.append('attachments', f));
    try {
      await api.post(`/messages/conversations/${activeConvId}/messages`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await Promise.all([reloadMsgs(activeConvId), reloadConvs()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Senden fehlgeschlagen');
    }
  };

  const deleteMessage = async (msgId) => {
    if (!window.confirm('Nachricht für alle löschen? (DSGVO: Inhalt wird entfernt, Audit-Eintrag bleibt)')) return;
    try {
      await api.delete(`/messages/messages/${msgId}`);
      await reloadMsgs(activeConvId);
      toast.success('Nachricht gelöscht');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const archiveConv = async () => {
    if (!activeConvId) return;
    if (!window.confirm('Diese Konversation archivieren? (Sie bleibt erhalten und kann wieder eingeblendet werden.)')) return;
    try {
      await api.post(`/messages/conversations/${activeConvId}/archive`);
      setActiveConvId(null);
      await reloadConvs();
      toast.success('Konversation archiviert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archivieren fehlgeschlagen');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-4" data-testid="messages-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-7 h-7 text-brand-red" /> Nachrichten
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              DSGVO-konform · Soft-Delete · Aufbewahrungsfrist 365 Tage · Audit-Log
            </p>
          </div>
          <Button onClick={() => setNewDialogOpen(true)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="messages-new-conv-btn">
            <Plus className="w-4 h-4 mr-1" /> Neue Nachricht
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden grid grid-cols-1 md:grid-cols-[340px_1fr] min-h-[72vh]">
          {/* ConversationList */}
          <aside className="border-r border-slate-200 flex flex-col min-h-0">
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input placeholder="Chats durchsuchen" className="pl-8" disabled />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="messages-conv-list">
              {loadingConvs ? (
                <div className="p-6 text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline mr-1 animate-spin" /> Laden…</div>
              ) : conversations.length === 0 ? (
                <div className="p-6 text-slate-400 text-sm text-center">
                  Noch keine Konversationen. Starte deine erste mit „Neue Nachricht".
                </div>
              ) : (
                conversations.map((c) => (
                  <ConvListItem key={c.id}
                                conv={c}
                                currentUserId={currentUserId}
                                active={c.id === activeConvId}
                                onClick={() => setActiveConvId(c.id)} />
                ))
              )}
            </div>
          </aside>

          {/* Thread */}
          <section className="flex flex-col min-h-0">
            {activeConv ? (
              <>
                <header className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {activeConv.kind === 'group'
                        ? activeConv.title
                        : activeConv.participants.filter((p) => p.id !== currentUserId)
                            .map((p) => p.full_name || p.username).join(', ')}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <UsersIcon className="w-3 h-3" />
                      {activeConv.participants.length} Teilnehmer:innen
                      {activeConv.kind === 'group' && ` · Gruppe`}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={archiveConv} data-testid="messages-archive-btn">
                    <Archive className="w-4 h-4 mr-1" /> Archivieren
                  </Button>
                </header>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="messages-thread">
                  {loadingMsgs && messages.length === 0 && (
                    <div className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Nachrichten werden geladen…</div>
                  )}
                  {messages.map((m) => (
                    <MessageRow key={m.id} msg={m} currentUserId={currentUserId} onDelete={() => deleteMessage(m.id)} />
                  ))}
                </div>

                <Composer onSend={sendMessage} />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>Wähle eine Konversation links — oder starte eine neue.</p>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {newDialogOpen && (
        <NewConvDialog
          onClose={() => setNewDialogOpen(false)}
          onCreated={(id) => { setActiveConvId(id); setNewDialogOpen(false); reloadConvs(); }}
        />
      )}
    </Layout>
  );
}

function ConvListItem({ conv, currentUserId, active, onClick }) {
  const others = conv.participants.filter((p) => p.id !== currentUserId);
  const display = conv.kind === 'group' ? (conv.title || 'Gruppe')
    : others.map((p) => p.full_name || p.username).join(', ') || '—';
  return (
    <button
      onClick={onClick}
      data-testid={`messages-conv-${conv.id}`}
      className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-100 transition-colors ${active ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-xs font-bold
                      ${conv.kind === 'group' ? 'bg-brand-red/10 text-brand-red' : 'bg-brand-red/10 text-brand-red'}`}>
        {conv.kind === 'group' ? <UsersIcon className="w-5 h-5" /> : initials(display)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm font-medium truncate ${active ? 'text-brand-red' : 'text-slate-900'}`}>{display}</p>
          <span className="text-xs text-slate-400 shrink-0">{formatMessageTime(conv.last_message_at || conv.created_at)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500 truncate">{conv.last_message_preview || <span className="italic text-slate-400">Noch keine Nachrichten</span>}</p>
          {conv.unread_count > 0 && (
            <Badge className="bg-brand-red text-white shrink-0" data-testid={`messages-unread-${conv.id}`}>
              {conv.unread_count}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

function MessageRow({ msg, currentUserId, onDelete }) {
  const isOwn = msg.sender.id === currentUserId;
  const deleted = !!msg.deleted_at;
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`messages-msg-${msg.id}`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${isOwn ? 'bg-brand-red text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
        {!isOwn && (
          <p className="text-xs font-semibold mb-1 text-brand-red">{msg.sender.full_name || msg.sender.username}</p>
        )}
        {deleted ? (
          <p className="italic opacity-70 text-sm">Nachricht gelöscht</p>
        ) : (
          <>
            {msg.body && <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>}
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {msg.attachments.map((a) => <AttachmentRow key={a.id} att={a} light={isOwn} />)}
              </div>
            )}
          </>
        )}
        <div className={`flex items-center gap-1 mt-1 text-[10px] ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>
          <span>{formatMessageTime(msg.created_at)}</span>
          {isOwn && !deleted && (
            <>
              <MessageDeliveryStatus message={msg} />
              <button onClick={onDelete} className="ml-1 hover:underline" title="Nachricht löschen">
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AttachmentRow({ att, light }) {
  const download = async () => {
    try {
      const r = await api.get(`/messages/attachments/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download fehlgeschlagen');
    }
  };
  return (
    <button onClick={download} className={`flex items-center gap-2 rounded-lg px-2 py-1 text-xs border ${light ? 'bg-white/15 border-white/40 hover:bg-white/25 text-white' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}>
      <Download className="w-3 h-3" />
      <span className="truncate max-w-[180px]">{att.filename}</span>
      <span className="opacity-70">{(att.size_bytes / 1024).toFixed(0)} KB</span>
    </button>
  );
}

function Composer({ onSend }) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (sending) return;
    setSending(true);
    try {
      await onSend(body, files);
      setBody(''); setFiles([]);
    } finally { setSending(false); }
  };

  const addFiles = (list) => {
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    const MAX = 20 * 1024 * 1024;
    const next = [];
    for (const f of list) {
      if (!allowed.includes(f.type)) { toast.error(`Dateityp nicht erlaubt: ${f.name}`); continue; }
      if (f.size > MAX) { toast.error(`Datei zu groß (>20 MB): ${f.name}`); continue; }
      next.push(f);
    }
    setFiles((prev) => [...prev, ...next]);
  };

  return (
    <form onSubmit={submit} className="p-3 border-t border-slate-200 bg-white space-y-2" data-testid="messages-composer">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <span key={`${f.name}-${f.size}-${f.lastModified}`} className="inline-flex items-center gap-1 bg-brand-red/5 text-brand-red text-xs rounded-full pl-3 pr-1 py-1 border border-brand-red/20">
              <Paperclip className="w-3 h-3" /> {f.name}
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="hover:bg-brand-red/10 rounded-full p-0.5">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button type="button" onClick={() => fileInputRef.current?.click()} className="w-9 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center shrink-0" title="Datei anhängen (PDF, JPG, PNG, WebP, max. 20 MB)" data-testid="messages-attach-btn">
          <Paperclip className="w-4 h-4 text-slate-600" />
        </button>
        <input ref={fileInputRef} type="file" hidden multiple accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => addFiles(e.target.files)} />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(e); }}
          rows={1}
          placeholder="Nachricht eingeben… (Strg+Enter zum Senden)"
          className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 max-h-40"
          data-testid="messages-composer-input"
        />
        <Button type="submit" disabled={sending || (!body.trim() && files.length === 0)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="messages-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </form>
  );
}

function NewConvDialog({ onClose, onCreated }) {
  const [kind, setKind] = useState('direct');
  const [title, setTitle] = useState('');
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get('/messages/directory', { params: { q, limit: 30 } })
        .then((r) => setUsers(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const toggle = (u) => {
    if (kind === 'direct') setSelected([u]);
    else setSelected((prev) => prev.find((x) => x.id === u.id)
      ? prev.filter((x) => x.id !== u.id)
      : [...prev, u]);
  };

  const submit = async () => {
    if (selected.length === 0) return toast.error('Bitte mindestens eine:n Empfänger:in wählen');
    if (kind === 'group' && !title.trim()) return toast.error('Bitte einen Gruppen-Titel eingeben');
    setCreating(true);
    try {
      const r = await api.post('/messages/conversations', {
        kind,
        title: kind === 'group' ? title.trim() : null,
        participant_ids: selected.map((u) => u.id),
      });
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="messages-new-dialog">
        <DialogHeader>
          <DialogTitle>Neue Nachricht</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button onClick={() => { setKind('direct'); setSelected(selected.slice(0, 1)); }} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${kind === 'direct' ? 'border-brand-red bg-brand-red/5 text-brand-red' : 'border-slate-200'}`}>1:1-Chat</button>
            <button onClick={() => setKind('group')} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${kind === 'group' ? 'border-brand-red bg-brand-red/5 text-brand-red' : 'border-slate-200'}`}>Gruppen-Chat</button>
          </div>
          {kind === 'group' && (
            <Input placeholder='Gruppen-Titel (z. B. „Kollegium")' value={title} onChange={(e) => setTitle(e.target.value)} />
          )}
          <Input placeholder="Empfänger:innen suchen" value={q} onChange={(e) => setQ(e.target.value)} data-testid="messages-new-search" />
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {users.length === 0 && <p className="p-3 text-xs text-slate-400">Keine Treffer</p>}
            {users.map((u) => {
              const isSel = !!selected.find((x) => x.id === u.id);
              return (
                <button key={u.id} onClick={() => toggle(u)} className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm ${isSel ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`} data-testid={`messages-new-pick-${u.username}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isSel ? 'bg-brand-red text-white' : 'bg-slate-100 text-slate-600'}`}>{initials(u.full_name || u.username)}</div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{u.full_name || u.username}</p>
                    <p className="text-xs text-slate-500">@{u.username}</p>
                  </div>
                  {isSel && <Check className="w-4 h-4 text-brand-red" />}
                </button>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div className="text-xs text-slate-500">Ausgewählt: {selected.map((u) => u.full_name || u.username).join(', ')}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={creating} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="messages-new-submit">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Starten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
